import { Component, OnInit, OnDestroy } from '@angular/core';
import { NotificationToastService } from 'src/app/services/notification-toast.service';
import { ActivatedRoute } from '@angular/router';
import { CashDeskService } from 'src/app/services/cash-desk.service';
import { AuthService } from 'src/app/services/auth.service';
import { PatientCashDeskSeachItem } from 'src/app/model/patient/patient-pending-cash-desk-search-item';
import { moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-patient-crm-cash-desk',
  templateUrl: './patient-crm-cash-desk.component.html',
  styleUrls: ['./patient-crm-cash-desk.component.css']
})
export class PatientCrmCashDeskComponent implements OnInit, OnDestroy {

  loadingPending: boolean;
  loadingMade: boolean;

  columnsDefinition: { field: string; title: string }[] = [
    { field: 'Protocol',                 title: 'Protocolo' },
    { field: 'Practice',                 title: 'Práctica' },
    { field: 'MedicalOrderAmount',       title: 'Importe' },
    { field: 'CashDeskStateDescription', title: 'Estado' },
  ];

  _columnOrder: string[] = this.columnsDefinition.map(c => c.field);
  columnWidths: { [field: string]: number } = {};

  // Pending grid
  pendingColumnFilters: { [field: string]: string } = {};
  _pendingSortColumn: string = '';
  _pendingSortDirection: 'asc' | 'desc' = 'asc';
  _pendingAllItems: PatientCashDeskSeachItem[] = [];
  pendingDataSource: PatientCashDeskSeachItem[] = [];

  // Made grid
  madeColumnFilters: { [field: string]: string } = {};
  _madeSortColumn: string = '';
  _madeSortDirection: 'asc' | 'desc' = 'asc';
  _madeAllItems: PatientCashDeskSeachItem[] = [];
  madeDataSource: PatientCashDeskSeachItem[] = [];

  private _resizeMoveHandler: (e: MouseEvent) => void;
  private _resizeUpHandler: (e: MouseEvent) => void;

  constructor(private notificationToastService: NotificationToastService,
    private cashDeskService: CashDeskService,
    private authService: AuthService,
    private route: ActivatedRoute) { }

  ngOnInit() {
    this.loadColumnState();
    this.loadingMade = true;
    this.loadingPending = true;
    this.route.params.subscribe((params) => {

      this.cashDeskService.getPatientMadeCashDeskSearchItem('',
        this.authService.getCompanyIds(), params['Id'], null).then((info) => {
          this._pendingAllItems = info != null ? info : [];
          this.applyColumnFilters('pending');
          this.loadingMade = false;
        }).catch((err) => this.notificationToastService
          .error(JSON.stringify(err.error), 'Cobranzas realizada'));

      this.cashDeskService.getPatientPendingCashDeskSearchItem('',
        this.authService.getCompanyIds(), params['Id'], null).then((info) => {
          this._madeAllItems = info != null ? info : [];
          this.applyColumnFilters('made');
          this.loadingPending = false;
        }).catch((err) => this.notificationToastService.error(JSON.stringify(err.error), 'Cobranzas pendiente'));

    });
  }

  get orderedColumnsDefinition(): { field: string; title: string }[] {
    return this._columnOrder
      .map(f => this.columnsDefinition.find(c => c.field === f))
      .filter(c => c != null);
  }

  formatCellValue(element: PatientCashDeskSeachItem, field: string): string {
    if (element == null) return '';
    const val = (element as any)[field];
    return val != null ? String(val) : '';
  }

  getSortableValue(element: PatientCashDeskSeachItem, field: string): any {
    return this.formatCellValue(element, field);
  }

  applyColumnFilters(grid: 'pending' | 'made') {
    const allItems = grid === 'pending' ? this._pendingAllItems : this._madeAllItems;
    const filters = grid === 'pending' ? this.pendingColumnFilters : this.madeColumnFilters;
    const sortColumn = grid === 'pending' ? this._pendingSortColumn : this._madeSortColumn;
    const sortDirection = grid === 'pending' ? this._pendingSortDirection : this._madeSortDirection;

    let filtered = allItems != null ? allItems.slice() : [];
    Object.keys(filters).forEach(field => {
      const term = filters[field];
      if (term && term.trim() !== '') {
        const lower = term.toLowerCase();
        filtered = filtered.filter(element => {
          const val = this.formatCellValue(element, field);
          return val.toLowerCase().includes(lower);
        });
      }
    });
    if (sortColumn) {
      filtered.sort((a, b) => {
        const av = this.getSortableValue(a, sortColumn);
        const bv = this.getSortableValue(b, sortColumn);
        if (av < bv) return sortDirection === 'asc' ? -1 : 1;
        if (av > bv) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    if (grid === 'pending') {
      this.pendingDataSource = filtered;
    } else {
      this.madeDataSource = filtered;
    }
  }

  loadColumnState() {
    try {
      const orderRaw = sessionStorage.getItem('patient-crm-cash-desk.columns.v1');
      if (orderRaw) {
        const saved: string[] = JSON.parse(orderRaw);
        const valid = saved.filter(f => this.columnsDefinition.some(c => c.field === f));
        const missing = this.columnsDefinition.map(c => c.field).filter(f => !valid.includes(f));
        this._columnOrder = valid.concat(missing);
      }
      const widthsRaw = sessionStorage.getItem('patient-crm-cash-desk.column-widths.v1');
      if (widthsRaw) {
        this.columnWidths = JSON.parse(widthsRaw);
      }
    } catch (e) {}
  }

  saveColumnOrder() {
    sessionStorage.setItem('patient-crm-cash-desk.columns.v1', JSON.stringify(this._columnOrder));
  }

  saveColumnWidths() {
    sessionStorage.setItem('patient-crm-cash-desk.column-widths.v1', JSON.stringify(this.columnWidths));
  }

  getColumnWidth(field: string): number {
    return this.columnWidths[field] != null ? this.columnWidths[field] : 150;
  }

  sortByColumn(grid: 'pending' | 'made', field: string) {
    if (grid === 'pending') {
      if (this._pendingSortColumn === field) {
        this._pendingSortDirection = this._pendingSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this._pendingSortColumn = field;
        this._pendingSortDirection = 'asc';
      }
    } else {
      if (this._madeSortColumn === field) {
        this._madeSortDirection = this._madeSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this._madeSortColumn = field;
        this._madeSortDirection = 'asc';
      }
    }
    this.applyColumnFilters(grid);
  }

  getSortIcon(grid: 'pending' | 'made', field: string): string {
    const sortColumn = grid === 'pending' ? this._pendingSortColumn : this._madeSortColumn;
    const sortDirection = grid === 'pending' ? this._pendingSortDirection : this._madeSortDirection;
    if (sortColumn !== field) return 'unfold_more';
    return sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  onColumnFilterInput(grid: 'pending' | 'made', field: string, value: string) {
    if (grid === 'pending') {
      this.pendingColumnFilters[field] = value;
    } else {
      this.madeColumnFilters[field] = value;
    }
    this.applyColumnFilters(grid);
  }

  onHeaderDragStart(event: DragEvent, field: string) {
    event.dataTransfer.setData('text/plain', field);
    event.dataTransfer.effectAllowed = 'move';
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  onHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    const sourceField = event.dataTransfer.getData('text/plain');
    if (sourceField === targetField) return;
    const fromIndex = this._columnOrder.indexOf(sourceField);
    const toIndex = this._columnOrder.indexOf(targetField);
    if (fromIndex < 0 || toIndex < 0) return;
    moveItemInArray(this._columnOrder, fromIndex, toIndex);
    this.saveColumnOrder();
    this.applyColumnFilters('pending');
    this.applyColumnFilters('made');
  }

  onHeaderDragEnd() {}

  startResize(event: MouseEvent, field: string) {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = this.getColumnWidth(field);
    this._resizeMoveHandler = (e: MouseEvent) => {
      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
      this.columnWidths = Object.assign({}, this.columnWidths, { [field]: newWidth });
    };
    this._resizeUpHandler = () => {
      this.saveColumnWidths();
      window.removeEventListener('mousemove', this._resizeMoveHandler);
      window.removeEventListener('mouseup', this._resizeUpHandler);
    };
    window.addEventListener('mousemove', this._resizeMoveHandler);
    window.addEventListener('mouseup', this._resizeUpHandler);
  }

  ngOnDestroy() {
    if (this._resizeMoveHandler) window.removeEventListener('mousemove', this._resizeMoveHandler);
    if (this._resizeUpHandler) window.removeEventListener('mouseup', this._resizeUpHandler);
  }
}
